import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { message } from 'antd';
import { ApiOutlined, CloudServerOutlined } from '@ant-design/icons';
import { useUserSession } from '../../contexts/UserSessionContext';
import { useSearch } from '../../contexts/SearchContext';
import { useToolsListCache, invalidateToolsListCache } from '../../hooks/useToolsListCache';
import {
  createConnector,
  createMcpServer,
} from '../../services/api/agents';
import { parseMcpOAuthPostMessage } from '../../utils/mcpOAuth';
import SegmentedToggle from '../SegmentedToggle';
import AddActionDropdown from '../ui/AddActionDropdown';
import ToolsTable from './ToolsTable';
import AddToolModal from './AddToolModal';
import ToolDetailDrawer from './ToolDetailDrawer';
import { buildUnifiedToolRows, filterToolRows } from './toolsUtils';
import { countToolsForScope, filterToolRowsByScope } from './toolsScopeUtils';
import {
  buildHttpAuth,
  buildMcpAuth,
  handleMcpConnectResult,
  parseParametersSchema,
  DEFAULT_PARAMS_SCHEMA,
} from './toolFormUtils';
import '../../pages/DataCollections.css';
import './tools.css';

export default function ToolsPage() {
  const { keycloak, initialized } = useKeycloak();
  const [searchParams, setSearchParams] = useSearchParams();
  const { registerSearchHandler } = useSearch();
  const {
    userAssistants,
    platformAssistants,
    assistantsLoaded,
    refreshAssistants,
    isAdmin,
  } = useUserSession();

  const {
    connectors,
    mcpServers,
    nativeTools,
    loading,
    load,
  } = useToolsListCache(keycloak, { isAdmin });

  const [addType, setAddType] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [localFilter, setLocalFilter] = useState('');
  const [scopeTab, setScopeTab] = useState('personal');

  const scopeContext = useMemo(() => ({
    userAssistants: userAssistants || [],
    platformAssistants: platformAssistants || [],
    isAdmin,
    assistantsLoaded,
  }), [userAssistants, platformAssistants, isAdmin, assistantsLoaded]);

  const assistants = useMemo(() => {
    const byId = new Map();
    [...(userAssistants || []), ...(platformAssistants || [])].forEach((a) => {
      if (a?.id != null) byId.set(a.id, a);
    });
    return Array.from(byId.values()).sort((a, b) =>
      String(a.name || a.id).localeCompare(String(b.name || b.id)));
  }, [userAssistants, platformAssistants]);

  const assistantsInScope = useMemo(() => {
    if (scopeTab === 'platform' && isAdmin) return platformAssistants || [];
    let rows = userAssistants || [];
    if (scopeTab === 'personal') return rows.filter((a) => !a.group_id);
    if (scopeTab === 'group') return rows.filter((a) => a.group_id);
    return rows;
  }, [scopeTab, isAdmin, userAssistants, platformAssistants]);

  const rows = useMemo(
    () => buildUnifiedToolRows({ connectors, mcpServers, nativeTools, isAdmin }),
    [connectors, mcpServers, nativeTools, isAdmin],
  );

  const personalCount = useMemo(
    () => countToolsForScope(rows, 'personal', scopeContext),
    [rows, scopeContext],
  );
  const groupCount = useMemo(
    () => countToolsForScope(rows, 'group', scopeContext),
    [rows, scopeContext],
  );
  const platformCount = useMemo(
    () => countToolsForScope(rows, 'platform', scopeContext),
    [rows, scopeContext],
  );

  const scopeTabOptions = useMemo(() => {
    const opts = [
      { value: 'personal', text: 'Personal', count: personalCount },
      { value: 'group', text: 'Group', count: groupCount },
    ];
    if (isAdmin) opts.push({ value: 'platform', text: 'Platform', count: platformCount });
    return opts;
  }, [personalCount, groupCount, platformCount, isAdmin]);

  const scopedRows = useMemo(
    () => filterToolRowsByScope(rows, scopeTab, scopeContext),
    [rows, scopeTab, scopeContext],
  );

  const filteredRows = useMemo(
    () => filterToolRows(scopedRows, localFilter),
    [scopedRows, localFilter],
  );

  const refreshAll = useCallback(async (force = true) => {
    invalidateToolsListCache();
    await load(force);
    refreshAssistants();
  }, [load, refreshAssistants]);

  useEffect(() => {
    if (initialized && keycloak?.authenticated) {
      load();
    }
  }, [initialized, keycloak?.authenticated, load]);

  useEffect(() => {
    const toolId = searchParams.get('tool');
    const toolType = searchParams.get('type');
    if (!toolId || !toolType || loading) return;
    const row = rows.find((r) => String(r.id) === String(toolId) && r.rowType === toolType);
    if (row) {
      setDetailRow(row);
      setDetailOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, rows, loading, setSearchParams]);

  useEffect(() => {
    const addParam = searchParams.get('add');
    if (addParam === 'http' || addParam === 'mcp') {
      setAddType(addParam);
      setSearchParams({}, { replace: true });
    } else if (addParam === '1') {
      setAddType('http');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleMcpOAuthResult = useCallback((data) => {
    if (data.status === 'ok' && data.serverId) {
      message.success('MCP OAuth connected');
      refreshAll(true);
      setDetailRow({ rowType: 'mcp', id: Number(data.serverId), raw: {} });
      setDetailOpen(true);
    } else if (data.status === 'error') {
      message.error(data.message || 'MCP OAuth failed');
    }
  }, [refreshAll]);

  useEffect(() => {
    const oauthResult = searchParams.get('mcp_oauth');
    if (!oauthResult) return;
    if (window.opener && window.opener !== window) {
      window.opener.postMessage({
        type: 'focusml_mcp_oauth',
        status: oauthResult,
        serverId: searchParams.get('server_id'),
        message: searchParams.get('message'),
      }, '*');
      window.close();
      return;
    }
    if (!keycloak?.authenticated) return;
    handleMcpOAuthResult({
      status: oauthResult,
      serverId: searchParams.get('server_id'),
      message: searchParams.get('message'),
    });
    setSearchParams({}, { replace: true });
  }, [searchParams, keycloak?.authenticated, handleMcpOAuthResult, setSearchParams]);

  useEffect(() => {
    const onOAuthMessage = (event) => {
      const data = parseMcpOAuthPostMessage(event);
      if (data) handleMcpOAuthResult(data);
    };
    window.addEventListener('message', onOAuthMessage);
    return () => window.removeEventListener('message', onOAuthMessage);
  }, [handleMcpOAuthResult]);

  useEffect(() => {
    return registerSearchHandler('Tools', (qRaw) => {
      const q = qRaw.trim().toLowerCase();
      setLocalFilter(q);
      if (!q) return [];
      return filterToolRows(scopedRows, q)
        .slice(0, 8)
        .map((r) => ({
          id: r.key,
          title: r.name,
          subtitle: `${r.type} · ${r.toolCount} tools`,
          type: 'tool',
          action: () => {
            if (r.rowType !== 'native') {
              setDetailRow(r);
              setDetailOpen(true);
            }
          },
        }));
    });
  }, [registerSearchHandler, scopedRows]);

  const openDetail = (row) => {
    setDetailRow(row);
    setDetailOpen(true);
  };

  const handleCreateHttp = async (values, createMode) => {
    try {
      const payload = {
        name: values.name,
        description: values.description,
        base_url: values.base_url,
        auth: buildHttpAuth(values),
      };
      if (createMode === 'openapi') {
        if (!values.openapi_url && !values.openapi_spec_text) {
          throw new Error('Provide an OpenAPI URL or paste a JSON spec');
        }
        if (values.openapi_spec_text) {
          payload.openapi_spec = JSON.parse(values.openapi_spec_text);
        } else if (values.openapi_url) {
          payload.openapi_url = values.openapi_url;
        }
      } else {
        const parameters_schema = parseParametersSchema(values.parameters_schema_text || DEFAULT_PARAMS_SCHEMA);
        payload.initial_tool = {
          name: values.tool_name,
          description: values.tool_description || values.tool_name,
          http_method: values.http_method || 'GET',
          path_template: values.path_template,
          parameters_schema,
          kind: values.kind || 'read',
        };
      }
      const result = await createConnector(keycloak, payload);
      message.success(`HTTP source created (${result.tool_count ?? 0} tools)`);
      setAddType(null);
      await refreshAll(true);
      if (result.id) {
        openDetail({ rowType: 'http', id: result.id, raw: result });
      }
    } catch (e) {
      message.error(e.message);
    }
  };

  const handleCreateMcp = async (values) => {
    try {
      const result = await createMcpServer(keycloak, {
        name: values.name,
        url: values.url,
        auth: buildMcpAuth(values),
      });
      setAddType(null);
      await refreshAll(true);
      handleMcpConnectResult(result, {
        onSuccess: () => {
          if (result.id) openDetail({ rowType: 'mcp', id: result.id, raw: result });
        },
        openDetailId: (id) => openDetail({ rowType: 'mcp', id, raw: result }),
      });
    } catch (e) {
      message.error(e.message);
    }
  };

  return (
    <div className="page-container">
      <div className="collections-page-header">
        <div className="page-header" style={{ flex: 1, minWidth: 0 }}>
          <h2 className="page-header-title">Tools</h2>
          <span className="page-header-subtitle">
            Connect external APIs and MCP servers so assistants can call tools during conversations.
          </span>
        </div>
        <AddActionDropdown
          label="Add tool"
          className="new-collection-btn"
          items={[
            {
              key: 'http',
              title: 'HTTP source',
              subtitle: 'OpenAPI spec or a custom REST endpoint.',
              icon: <ApiOutlined />,
              onClick: () => setAddType('http'),
            },
            {
              key: 'mcp',
              title: 'MCP server',
              subtitle: 'Remote Model Context Protocol server.',
              icon: <CloudServerOutlined />,
              onClick: () => setAddType('mcp'),
            },
          ]}
        />
      </div>

      <SegmentedToggle options={scopeTabOptions} value={scopeTab} onChange={setScopeTab} />

      <div className="collections-table-wrapper collections-table-animate">
        <ToolsTable
          rows={filteredRows}
          loading={loading}
          onRowClick={openDetail}
          onAdd={() => setAddType('http')}
        />
      </div>

      <AddToolModal
        open={Boolean(addType)}
        type={addType}
        onCancel={() => setAddType(null)}
        onCreateHttp={handleCreateHttp}
        onCreateMcp={handleCreateMcp}
      />

      <ToolDetailDrawer
        open={detailOpen}
        rowType={detailRow?.rowType}
        toolId={detailRow?.id}
        rowPreview={detailRow?.raw}
        keycloak={keycloak}
        assistants={assistantsInScope}
        assistantsLoaded={assistantsLoaded}
        onClose={() => { setDetailOpen(false); setDetailRow(null); }}
        onRefreshList={refreshAll}
      />
    </div>
  );
}
